/**
 * Tenant branding helper.
 *
 * Phase 2: after a successful login we fetch the logged-in tenant's branding
 * (theme colors + logo + brand name + favicon) and apply it to the UI. The
 * branding is cached in localStorage so that on a page reload the UI is themed
 * instantly (no flash) before the server is queried again.
 *
 * Before login (login page) and after logout we fall back to the platform
 * default "eye-clinic" branding.
 */

import { COLOR_BY_ENV } from '../styles/color';

export const TENANT_BRANDING_KEY = 'tenantBranding';

// Upload size limits (must match the backend validator).
export const LOGO_MAX = 1024 * 1024; // 1 MB
export const FAVICON_MAX = 300 * 1024; // 300 KB

// Read an image File into a data-URI string. Rejects with 'TOO_LARGE' if the
// file exceeds maxBytes (validated before reading to avoid loading huge files).
export const readImageFile = (file, maxBytes) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('NO_FILE'));
      return;
    }
    if (maxBytes && file.size > maxBytes) {
      reject(new Error('TOO_LARGE'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result); // "data:image/...;base64,..."
    reader.onerror = () => reject(reader.error || new Error('READ_ERROR'));
    reader.readAsDataURL(file);
  });

// Platform default ("eye-clinic") branding shown on the login page, on a fresh
// load with an empty cache, and after logout. Uses the default "dev" color set
// + the default logo/favicon. (Individual users don't know other tenants exist
// and can't fake another tenant's branding locally, so a generic default here
// is fine — no security concern.)
export const PLATFORM_BRANDING = {
  tenantCode: null,
  brandName: 'eye-clinic',
  logo: '/environments/logo.png',
  favicon: '/environments/favicon.ico',
  colorSet: COLOR_BY_ENV.dev, // default theme when there's no tenant branding
};

/**
 * Map the backend `theme` object onto the `colorSet` shape that getTheme()
 * expects. The shapes already line up; this keeps the mapping in one place so
 * the rest of the app never depends on the raw API contract.
 */
export const mapThemeToColorSet = (theme) => {
  if (!theme) return null;
  const { primary, containedButton, outlinedButton } = theme;
  return {
    primary,
    containedButton: containedButton || {},
    outlinedButton: outlinedButton || {},
  };
};

/**
 * Normalize a GET /tenants result into the branding object we persist + apply.
 */
export const buildBrandingFromTenant = (tenant) => {
  if (!tenant) return null;
  const { tenantCode, brandName, theme, assets } = tenant;
  const { logoBase64, faviconBase64 } = assets || {};

  return {
    tenantCode,
    brandName: brandName || PLATFORM_BRANDING.brandName,
    logo: logoBase64 || PLATFORM_BRANDING.logo,
    favicon: faviconBase64 || PLATFORM_BRANDING.favicon,
    colorSet: mapThemeToColorSet(theme),
  };
};

export const getCachedBranding = () => {
  try {
    const raw = localStorage.getItem(TENANT_BRANDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

export const setCachedBranding = (branding) => {
  try {
    localStorage.setItem(TENANT_BRANDING_KEY, JSON.stringify(branding));
  } catch (error) {
    // ignore storage errors (e.g. private mode / quota)
  }
};

export const clearCachedBranding = () => {
  try {
    localStorage.removeItem(TENANT_BRANDING_KEY);
  } catch (error) {
    // ignore storage errors
  }
};

/**
 * Apply the document-level branding side effects (tab title + favicon).
 * The logo and theme colors are applied through Redux so React re-renders.
 */
export const applyDocumentBranding = (branding) => {
  const { brandName, favicon } = branding || PLATFORM_BRANDING;

  if (brandName) document.title = brandName;

  if (favicon) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = favicon;
  }
};
